import type { TOC } from "@ember/component/template-only";
import mergeClasses from "glesys-controlpanel/modifiers/merge-classes";

import RadioCheckboxTile from "glesys-controlpanel/components/radio-checkbox-tile";
import type { InputRadioSignature } from "glesys-controlpanel/components/input-radio";
import InputRadio from "glesys-controlpanel/components/input-radio";
import AlertMessage from "glesys-controlpanel/components/alert-message";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { eq } from "ember-truth-helpers";
import { t } from "ember-intl";
import mibToGib from "glesys-controlpanel/helpers/g-mibtogib";
import Pill from "glesys-controlpanel/components/pill";

export const SelectablePlanSize: TOC<{
  Blocks: { default: [] };
  Element: InputRadioSignature["Element"];
}> = <template>
  <label
    class="relative cursor-pointer rounded-md p-2 col-span-full grid grid-cols-subgrid gap-2 even:bg-gray-50 odd:bg-white border-b last:border-b-0 border-gray-200 py-3 px-6 items-center"
  >
    <div class="w-12 peer">
      <InputRadio ...attributes />
    </div>
    <div class="absolute top-0 bottom-0 peer-has-checked:border-green-100 border-l-2 border-l-transparent" />
    {{yield}}
  </label>
</template>;

const PLAN_TYPE = {
  FLEXIBLE: "flexible",
  FIXED: "fixed",
} as const;

type PlanType = (typeof PLAN_TYPE)[keyof typeof PLAN_TYPE];

type PlanSize = {
  id: string;
  cpucores: number;
  memorysize: number;
  disksize: number;
};

type Plan = {
  id: string;
  name: string;
  sizes?: PlanSize[];
};

interface PlansSignature {
  Args: {
    plans: Plan[];
    selected: {
      type: PlanType;
      plan: Plan;
      size: PlanSize;
    };
    onTypeChange: (type: PlanType) => void;
    onPlanChange: (plan: Plan) => void;
    onSizeChange: (size: PlanSize) => void;
  };
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const Plans: TOC<PlansSignature> = <template>
  <div class="grid grid-cols-2 gap-2 mb-6" data-test-servers-create-plans {{mergeClasses}} ...attributes>
    <RadioCheckboxTile
      class="items-start"
      data-test-servers-create-plans-type="flexible"
      @group="server-plans-type"
      @value={{PLAN_TYPE.FLEXIBLE}}
      @selected={{@selected.type}}
      @onChange={{@onTypeChange}}
    >
      <div>
        <div class="flex items-center gap-2 mb-1">
          <p class="text-md font-bold">
            {{t "servers.create.fixed-plans.plans.title.flexible"}}
          </p>
          <Pill @uppercase={{false}} @text="x86 CPU" @size="small" />
        </div>
        <p class="text-gray-500 text-start">
          {{t "servers.create.fixed-plans.plans.description.flexible"}}
        </p>
      </div>
    </RadioCheckboxTile>
    <RadioCheckboxTile
      class="items-start"
      data-test-servers-create-plans-type="fixed"
      @group="server-plans-type"
      @value={{PLAN_TYPE.FIXED}}
      @selected={{@selected.type}}
      @onChange={{@onTypeChange}}
    >
      <div>
        <div class="flex items-center gap-2 mb-1">
          <p class="text-md font-bold">
            {{t "servers.create.fixed-plans.plans.title.fixed"}}
          </p>
          <Pill @color="indigo" @text="ARM CPU" @size="small" />
        </div>
        <p class="text-gray-500 text-start">
          {{t "servers.create.fixed-plans.plans.description.fixed"}}
        </p>
      </div>
    </RadioCheckboxTile>

    {{#if (eq @selected.type PLAN_TYPE.FIXED)}}
      <div class="border-b border-gray-200 flex">
        {{#each @plans as |plan|}}
          <label
            class="has-checked:border-b border-black has-checked:text-black! has-checked:font-semibold p-3 text-base text-gray-600 hover:text-gray-800 -mb-px"
          >
            <input
              class="appearance-none"
              data-test-servers-create-plans-plan={{plan.id}}
              type="radio"
              name="server-plans"
              value={{plan.id}}
              checked={{eq plan.id @selected.plan.id}}
              {{on "change" (fn @onPlanChange plan)}}
            />
            {{plan.name}}
          </label>
        {{/each}}
      </div>
    {{/if}}
  </div>

  {{#if @selected.plan.sizes}}
    <AlertMessage class="mb-6">
      {{t "servers.create.fixed-plans.fixed-plan-cant-be-changed"}}
    </AlertMessage>
    <div class="grid grid-cols-[min-content_1fr_1fr_1fr]" data-test-servers-create-plans-plan-sizes>
      <div class="col-span-full grid grid-cols-subgrid gap-2 mb-2">
        <div></div>
        <div class="text-sm font-normal text-gray-500 text-left">{{t "label.cpu"}}</div>
        <div class="text-sm font-normal text-gray-500 text-left">{{t "label.memory"}}</div>
        <div class="text-sm font-normal text-gray-500 text-left">{{t "label.storage"}}</div>
      </div>
      {{#each @selected.plan.sizes as |size|}}
        <SelectablePlanSize
          aria-label="{{t
            'servers.create.fixed-plans.aria-label-plan-size'
            cpu=size.cpucores
            memory=(mibToGib size.memorysize)
            storage=size.disksize
          }}"
          data-test-servers-create-plans-plan-size={{size.id}}
          name="server-plans-size-new"
          value={{size.id}}
          checked={{eq size.id @selected.size.id}}
          {{on "change" (fn @onSizeChange size)}}
        >
          <div class="flex items-center justify-start">
            {{t "servers.create.fixed-plans.cpu" cores=size.cpucores}}
          </div>
          <div class="flex items-center justify-start">
            {{t "servers.create.fixed-plans.memory" memory=(mibToGib size.memorysize)}}
          </div>
          <div class="flex items-center justify-start">
            {{t "servers.create.fixed-plans.storage" storage=size.disksize}}
          </div>
        </SelectablePlanSize>
      {{/each}}
    </div>
  {{/if}}
</template>;

export default Plans;
